<template>
    <!--h4 class="text-success text-center">Api integrated</h4-->

  <div class="p-3" v-if="loading">
      <CardLoader class="m-3" />
    <CardLoader class="m-3" />
  </div>

    <div class="w-100 p-3" v-else>
        <div class="min-vh-100 bg-light">
            <div class="">
                <!-- Search Bar --> 
                <div v-if="!loading && (organizations.length > 0 || searchInput)"
                    class="input-group my-4 d-flex flex-nowrap justify-content-end gap-1">
                    <div class="px-2">
                        <material-input id="search" type="text" v-model="searchInput" size="lg"
                            @keyup.enter="applySearch" />
                    </div>
                    <div>
                        <button class="btn btn-success z-0" @click="applySearch">
                            <i class="material-icons">search</i>
                        </button>
                    </div>
                </div>

                <!-- No Data State -->
                <div v-if="!loading && organizations.length === 0"
                    class="d-flex flex-column align-items-center justify-content-center" style="min-height: 60vh;">
                    <svg width="200" height="200" viewBox="0 0 200 200" fill="none" xmlns="http://www.w3.org/2000/svg">
                        <circle cx="100" cy="100" r="80" fill="#f0f0f0" />
                        <path d="M60 90 L80 110 L140 50" stroke="#d0d0d0" stroke-width="8" stroke-linecap="round"
                            stroke-linejoin="round" fill="none" />
                        <circle cx="100" cy="100" r="90" stroke="#e0e0e0" stroke-width="2" fill="none"
                            stroke-dasharray="5,5">
                            <animateTransform attributeName="transform" attributeType="XML" type="rotate"
                                from="0 100 100" to="360 100 100" dur="20s" repeatCount="indefinite" />
                        </circle>
                    </svg>
                    <h3 class="text-secondary mt-4 mb-2">{{ searchInput ? 'No Matching Organizations Found' : 'No Organizations Found' }}</h3>
                    <p class="text-muted text-center" style="max-width: 400px;">
                        {{ searchInput ? `No results found for "${searchInput}". Try a different search term.` : 'There are currently no organizations available. Start by creating your first organization to get started.' }}
                    </p>
                    <button v-if="!searchInput" class="btn btn-success mt-3 px-4">
                        <i class="material-icons me-2">add</i>
                        Create Organization
                    </button>
                    <button v-else class="btn btn-outline-secondary mt-3 px-4" @click="searchInput = ''; applySearch()">
                        Clear Search
                    </button>
                </div>

                <!-- Organizations Content -->
                <div v-else-if="!loading && organizations.length > 0" class="skills-container">
                    <div v-for="organization in organizations" :key="organization.organizationid"
                        class="skill-row mb-5">
                        <!-- Organization Header -->
                        <div class="card">
                            <div class="card-header bg-success text-white">
                                <div class="d-flex justify-content-between align-items-center">
                                    <div>
                                        <h3 class="text-white h5 mb-1">{{ organization.organizationid }} -
                                            {{ organization.organizationname }}</h3>
                                        <p class="mb-0 small opacity-75">{{ organization.description }}</p>
                                    </div>
                                    <span :class="[
                                        'badge',
                                        organization.status === 'active' ? 'border border-1 border-white' : 'bg-danger'
                                    ]">
                                        {{ organization.status }}
                                    </span>
                                </div>
                            </div>
                        </div>

                        <!-- Organization Details -->
                        <div class="card mb-3">
                            <div class="card-header">
                                <div class="d-flex justify-content-between align-items-center">
                                    <p class="m-0 large h6">Organization Details</p>
                                    <p class="m-0 large"><b>Email:</b> {{ organization.email }}</p>
                                    <p class="m-0 large"><b>Phone Number:</b> {{ organization.phone }}</p>
                                    <button class="btn btn-success btn-sm m-0"
                                        @click="editOrganization(organization.organizationid)">
                                        <i class="material-icons text-lg text-white">edit</i>
                                    </button>
                                </div>
                            </div>
                        </div>

                        <!-- Locations Table -->
                        <div class="table-responsive" style="max-height: 600px; overflow-x: auto;">
                            <BaseTable :rows="organization.locations" :columns="columns" :searchable="false"
                                :selectable="false" :pagination="false" heading="Locations">
                                <template #buttons>
                                    <button class="btn btn-sm btn-info"
                                        @click="openAddNewLocationModal(organization.organizationid)">
                                        <i class="material-icons text-lg text-white">add</i> Add New
                                    </button>
                                </template>
                                <template #actions="{ row }">
                                    <button class="btn btn-sm my-0 py-0" @click="editLocation(row.locationid)">
                                        <i class="material-icons text-lg text-orange">edit</i>
                                    </button>
                                    <button class="btn btn-sm my-0 py-0" @click="openDeleteModal(row)">
                                        <i class="material-icons text-lg text-danger">delete</i>
                                    </button>
                                    <button class="btn btn-sm my-0 py-0" @click="viewLocation(row.locationid)">
                                        <i class="material-icons text-lg text-blue">visibility</i>
                                    </button>
                                </template>
                            </BaseTable>
                        </div>
                    </div>
                </div>
            </div>
        </div>

        <!-- Pagination Footer -->
        <div class="px-4 py-2">
            <PaginationFooter :pagination="pagination" @changePage="loadPage" @updatePageSize="updatePageSize" />
        </div>
    </div>

    <!-- Modals -->
    <ContentModal v-model="showModal" title="Location">
        <template #default>
            <b>firstname</b> - replaced with user's firstname <br>
            <b>lastname</b> - replaced with user's lastname <br>
            <b>email</b> - replaced with user's email address <br>
            <b>ical</b> - replaced with user's iCal subscription URL <br>
            <b>signup</b> - replaced with list of Sign-up only assignments for the next 60 days (email only). <br>
            <b>preferences</b> - replaced with user Availability, Preferences, Scheduling rules.
        </template>
    </ContentModal>

    <ContentModal v-model="showAddNewLocationModal" title="Add New Location">
        <template #default>
            <form>
                <div class="row">
                    <div class="col-md-6">
                        <div class="mb-3">
                            <material-input id="locationname" type="text" label="Location Name" name="locationname"
                                v-model="organizationLocation.locationname" />
                        </div>
                    </div>
                    <div class="col-md-6 d-flex align-items-end">
                        <div class="mb-3">
                            <label>i.e. 'Atlanta location'</label>
                        </div>
                    </div>
                </div>

                <div class="row">
                    <div class="col-12">
                        <div class="mb-3">
                            <material-input id="description" type="text" label="Description" name="description"
                                v-model="organizationLocation.description" />
                        </div>
                    </div>
                </div>

                <div class="row">
                    <div class="col-md-6">
                        <div class="mb-3">
                            <material-input id="email" type="email" label="Email" name="email"
                                v-model="organizationLocation.email" />
                        </div>
                    </div>
                    <div class="col-md-6">
                        <div class="mb-3">
                            <material-input id="phone" type="tel" label="Phone Number" name="phone"
                                v-model="organizationLocation.phone" />
                        </div>
                    </div>
                </div>

                <div class="row">
                    <div class="col-md-6">
                        <div class="mb-3">
                            <MaterialSelect id="country" label="Country" :options="countriesOptions"
                                v-model="organizationLocation.country" />
                        </div>
                    </div>
                    <div class="col-md-6">
                        <div class="mb-3">
                            <MaterialSelect id="timezone" label="Timezone" :options="timezonesOptions"
                                v-model="organizationLocation.timezone" />
                        </div>
                    </div>
                </div>
            </form>
        </template>
        <template #footer>
            <!-- <MaterialButton type="submit" variant="primary" class="px-4 my-0" @click="saveOrganizationLocation">
                Save
            </MaterialButton> -->
            <MaterialButton type="submit" variant="primary" class="px-4" :disabled="isSaving"
                @click="saveOrganizationLocation">
                <span v-if="isSaving" class="spinner-border spinner-border-sm me-2" role="status"
                    aria-hidden="true"></span>
                <i v-else class="material-icons text-lg me-1">save</i>
                {{ isSaving ? 'Saving...' : 'Save' }}
            </MaterialButton>

        </template>
    </ContentModal>

    <!-- Delete Location Modal -->
    <DeleteModal v-model:show="showDeleteModal" :itemName="selectedLocation?.locationname || 'this location'" @confirm="deleteLocation" />

    <!-- Delete error safeguard -->
    <div v-if="deleteError" class="delete-error-overlay">
      <div class="delete-error-card">
        <div class="delete-error-header">
          <i class="material-icons">shield</i>
          <span>Deletion Safeguard</span>
          <button class="delete-error-close" @click="deleteError = null">&times;</button>
        </div>
        <div class="delete-error-body">
          <div class="delete-error-icon-wrap">
            <i class="material-icons">error_outline</i>
          </div>
          <p class="delete-error-msg">{{ deleteError }}</p>
          <div class="delete-error-help">
            <h6><i class="material-icons">lightbulb</i> What you need to do</h6>
            <ol>
              <li>Go to <strong>Edit Location</strong> and review all associated data (users, activities, schedules).</li>
              <li>Remove or reassign any <strong>users</strong>, <strong>activities</strong>, and <strong>schedules</strong> linked to this location.</li>
              <li>Set the location status to <strong>Inactive</strong> if it is currently active.</li>
              <li>Once all dependencies are cleared, return here and delete the location.</li>
            </ol>
            <p class="delete-error-note">
              <i class="material-icons" style="font-size:14px;vertical-align:middle;">info</i>
              This safeguard is in place to prevent accidental deletions that could affect users, activities, and scheduling across the organization.
            </p>
          </div>
        </div>
        <div class="delete-error-footer">
          <button class="btn btn-sm btn-primary" @click="deleteError = null">Got it</button>
        </div>
      </div>
    </div>

    <!-- View Location Modal -->
    <ContentModal v-model="showViewLocationModal" title="View Location" size="2xl">
        <ViewLocation v-if="showViewLocationModal" :locationId="selectedLocationId" />
    </ContentModal>
</template>

<script setup>
import { ref, onMounted, computed } from 'vue'
import { useStore } from 'vuex'
import { useRouter } from 'vue-router'
import { useBreadcrumb } from '@/composables/useBreadcrumb'
import BaseTable from "@/components/Table/Table.vue"
import ContentModal from "@/components/Modal/ContentModal.vue"
import DeleteModal from "@/components/Modal/ConfirmationModal.vue"
import MaterialInput from "@/components/common/MaterialInput.vue"
import MaterialSelect from "@/components/common/MaterialSelect.vue"
import MaterialButton from "@/components/common/MaterialButton.vue"
import CardLoader from '../../components/Loader/CardLoader.vue'
import PaginationFooter from "../../components/PageLayout/PaginationFooter.vue"
import ViewLocation from '@/modules/locations/ViewLocation.vue'
import { useToast } from 'vue-toast-notification'

const store = useStore()
const router = useRouter()
const toast = useToast()
const { setTrail, pushCrumb } = useBreadcrumb()

// Loading state
const loading = ref(false)
const isSaving = ref(false)

// Modal states
const showModal = ref(false)
const showAddNewLocationModal = ref(false)
const showDeleteModal = ref(false)
const showViewLocationModal = ref(false)
const selectedLocation = ref(null)
const selectedLocationId = ref(null)
const deleteError = ref(null)
const currentOrgId = ref(null)

// Pagination states
const currentPage = ref(1)
const perPage = ref(10)
const searchInput = ref("")

// Computed from Vuex store
const organizations = computed(() => store.getters["allOrganizations"])
const pagination = computed(() => store.getters["organizationsPagination"])

/**
 * ✅ Fetch organizations with current page and search
 */
const fetchOrganizations = async () => {
    console.log('Fetching organizations with:', {
        search: searchInput.value,
        pageNumber: currentPage.value,
        pageSize: perPage.value,
    })

    loading.value = true
    try {
        await store.dispatch("fetchOrganizations", {
            search: searchInput.value,
            pageNumber: currentPage.value,
            pageSize: perPage.value,
        })

        console.log('Organizations after fetch:', organizations.value)
        console.log('Pagination after fetch:', pagination.value)
    } catch (err) {
        console.error("Error loading organizations:", err)
    } finally {
        loading.value = false
    }
}

/**
 * ✅ Load a specific page
 */
const loadPage = async (page) => {
    if (!page) return
    currentPage.value = page
    await fetchOrganizations()
}

/**
 * ✅ Update page size and reset to page 1
 */
const updatePageSize = async (size) => {
    perPage.value = size
    currentPage.value = 1
    await fetchOrganizations()
}

/**
 * ✅ Apply search and reset to page 1
 */
const applySearch = async () => {
    currentPage.value = 1
    await fetchOrganizations()
}

// Lifecycle hook - fetch organizations when page loads
onMounted(async () => {
    loading.value = true
    try {
        // Fetch in parallel but handle errors individually
        const [orgs] = await Promise.allSettled([
            fetchOrganizations(),
            store.dispatch("getCountries"),
            store.dispatch("getTimezones")
        ])

        if (orgs.status === 'rejected') {
            console.error('Failed to load organizations:', orgs.reason)
        }
    } finally {
        loading.value = false
    }
})

const organizationLocation = ref({
    locationname: "",
    description: "",
    email: "",
    phone: "",
    country: "",
    timezone: ""
})

// Columns for BaseTable
const columns = ref([
    { key: "locationid", label: "ID", sortable: true },
    { key: "locationname", label: "Name", sortable: true },
    { key: "description", label: "Description", sortable: true },
    { key: "status", label: "Status", sortable: true },
    { key: "phone", label: "Phone", sortable: true },
    { key: "email", label: "Email", sortable: true },
    { key: "country", label: "Country", sortable: true },
    { key: "time_zone", label: "Timezone", sortable: true },
    { key: "createddtm", label: "Created Date", sortable: true },
    //{ key: "autoenginestartdt", label: "Engine Start", sortable: true },
    //{ key: "autoengineenddt", label: "Engine End", sortable: true },
    { key: "priority", label: "Priority", sortable: true },
    { key: "persons_count", label: "#Persons", sortable: true },
    { key: "volunteers_count", label: "#Vols", sortable: true },
])

// Event methods
const editOrganization = (id) => {
    const org = organizations.value.find(o => o.organizationid === id)
    setTrail([
        { label: 'Organizations', icon: 'business', route: { name: 'ManageOrganization' } },
        { label: org?.organizationname || `Organization #${id}`, icon: 'edit' },
    ])
    router.push({ name: "EditOrganization", params: { organizationID: id } })
}

const viewLocation = (locationId) => {
    selectedLocationId.value = String(locationId)
    showViewLocationModal.value = true
}

const openAddNewLocationModal = (orgId) => {
    currentOrgId.value = orgId
    organizationLocation.value = {
        locationname: "",
        description: "",
        email: "",
        phone: "",
        country: "",
        timezone: ""
    }
    showAddNewLocationModal.value = true
}

const openDeleteModal = (row) => {
    selectedLocation.value = row
    showDeleteModal.value = true
}

const deleteLocation = async () => {
    if (!selectedLocation.value) return;

    try {
        const response = await store.dispatch("deleteLocation", selectedLocation.value.locationid);

        // Handle response structure: response.output.message or response.message
        const output = response?.output || response;
        const message = output?.message || response?.message;
        const returncode = output?.returncode || response?.returncode;

        // Check response for success (returncode === 0 or "0" means success)
        const isSuccess = returncode === 0 || returncode === "0" || message?.toLowerCase().includes("success");

        if (isSuccess) {
            const successMessage = message || "Location deleted successfully";
            toast.success(successMessage, { position: "top-right" });
            // Refresh the organizations list to update locations
            await fetchOrganizations();
        } else {
            const errorMessage = message || "Failed to delete location";
            toast.error(errorMessage, { position: "top-right" });
        }

        // Close modal and reset selected location
        showDeleteModal.value = false;
        selectedLocation.value = null;
    } catch (error) {
        console.error("Error deleting location:", error);
        const errorMessage = error?.response?.data?.output?.message || 
                            error?.response?.data?.error ||
                            error?.response?.data?.message || 
                            error?.message || 
                            "Failed to delete location";
        const msgLower = errorMessage.toLowerCase();
        if (msgLower.includes('cannot delete') || msgLower.includes('assigned') || msgLower.includes('in use')) {
            deleteError.value = errorMessage;
        } else {
            toast.error(errorMessage, { position: "top-right" });
        }
        showDeleteModal.value = false;
        selectedLocation.value = null;
    }
}

const saveOrganizationLocation = async () => {
    isSaving.value = true  
    try {
        const res = await store.dispatch("createLocation", {
            ...organizationLocation.value,
            organizationid: currentOrgId.value
        })

        pushCrumb({ label: organizationLocation.value.locationname || 'New Location', icon: 'location_on' })
        router.push({ name: "EditLocation", params: { locationID: res.output.location.locationid } })
    } catch (error) {
        console.error("Error saving organization location:", error)
    } finally {
        isSaving.value = false 
        showAddNewLocationModal.value = false
    }
}


function editLocation(id) {
    pushCrumb({ label: `Location #${id}`, icon: 'location_on' })
    router.push({ name: "EditLocation", params: { locationID: id } })
}

const countriesOptions = computed(() => {
    const countries = store.getters["stCountries"] || {}
    return Object.keys(countries).map(key => ({
        label: countries[key],
        value: key
    }))
})

const timezonesOptions = computed(() => {
    const zones = store.getters["stTimezones"] || {}
    return Object.keys(zones).map(key => ({
        label: zones[key],
        value: key
    }))
})
</script>

<style scoped>
.delete-error-overlay {
  position: fixed;
  inset: 0;
  z-index: 1060;
  display: flex;
  align-items: center;
  justify-content: center;
  background: rgba(0,0,0,0.45);
  animation: fadeIn .2s;
}
.delete-error-card {
  background: #fff;
  border-radius: 16px;
  box-shadow: 0 12px 40px rgba(0,0,0,0.18);
  max-width: 480px;
  width: 92%;
  overflow: hidden;
  animation: slideUp .25s ease-out;
}
.delete-error-header {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 14px 20px;
  background: linear-gradient(135deg, #ef5350 0%, #c62828 100%);
  color: #fff;
  font-weight: 700;
  font-size: 1rem;
}
.delete-error-header i { font-size: 22px; }
.delete-error-close {
  margin-left: auto;
  background: none;
  border: none;
  color: #fff;
  font-size: 22px;
  cursor: pointer;
  line-height: 1;
  opacity: .8;
}
.delete-error-close:hover { opacity: 1; }
.delete-error-body {
  padding: 24px 20px 12px;
  text-align: center;
}
.delete-error-icon-wrap {
  margin-bottom: 12px;
}
.delete-error-icon-wrap i {
  font-size: 48px;
  color: #ef5350;
}
.delete-error-msg {
  font-size: 1.05rem;
  font-weight: 600;
  color: #344767;
  margin-bottom: 16px;
}
.delete-error-help {
  text-align: left;
  background: #f8f9fa;
  border-radius: 10px;
  padding: 16px;
}
.delete-error-help h6 {
  display: flex;
  align-items: center;
  gap: 6px;
  font-weight: 700;
  color: #1976d2;
  margin-bottom: 10px;
  font-size: 0.88rem;
}
.delete-error-help h6 i { font-size: 18px; }
.delete-error-help ol {
  padding-left: 1.25rem;
  margin-bottom: 12px;
  font-size: 0.85rem;
  color: #495057;
}
.delete-error-help ol li {
  margin-bottom: 6px;
}
.delete-error-note {
  font-size: 0.78rem;
  color: #7b809a;
  margin: 0;
  padding: 8px 10px;
  background: #e8f4fd;
  border-radius: 6px;
  border-left: 3px solid #1976d2;
}
.delete-error-footer {
  padding: 12px 20px 16px;
  text-align: center;
}
@keyframes fadeIn { from { opacity: 0; } to { opacity: 1; } }
@keyframes slideUp { from { transform: translateY(20px); opacity: 0; } to { transform: translateY(0); opacity: 1; } }
.hover-bg-secondary:hover {
    background-color: rgba(255, 255, 255, 0.1) !important;
}

.hover-shadow:hover {
    box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
    transition: box-shadow 0.2s ease;
}

.cursor-pointer {
    cursor: pointer;
}

.skill-row {
    border-left: 4px solid #6f42c1;
    padding-left: 1rem;
}
</style>
